/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of
 * Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained.
 */

import { createLogger, Logger, LoggerLevel } from '@quickplay/foundation';
import { IMAAdRequest } from '../../../models/csai/IMAAdRequest';
import { QPPrefetchManager } from './QPPrefetchManager';
import { ContentAdsManagerResolver } from './ContentAdsManagerResolver';

type AdEventListener = (event: any) => void;

export interface QPAdsManagerOptions {
  request: IMAAdRequest;
  imaSDK: any;
  logger?: Logger;
  onAdError?: (error: unknown) => void;
  eventListeners?: Array<{
    eventType: google.ima.AdEvent.Type | google.ima.AdErrorEvent.Type;
    listener: AdEventListener;
    useCapture?: boolean;
  }>;
  autoPlayAdBreaks?: boolean;
  onAdsManagerActivated?: (AdsManagerRole: AdsManagerRole) => void;
}

/**
 * PREROLL:
 * The prefetched pre-roll AdsManager.
 *
 * CONTENT:
 * The AdsManager used for the remaining content ad breaks.
 */
export type AdsManagerRole = 'PREROLL' | 'CONTENT';

/**
 * A player listener that survives AdsManager swaps.
 */
interface RegisteredListener {
  eventType: google.ima.AdEvent.Type | google.ima.AdErrorEvent.Type;
  listener: AdEventListener;
  useCapture: boolean;
}

/**
 * Owns the AdsManager exposed to the player.
 *
 * With pre-roll prefetch enabled:
 *
 * 1. Pre-roll and content AdsManagers are fetched in parallel.
 * 2. Pre-roll becomes the active manager immediately.
 * 3. Content manager is kept ready in the background.
 * 4. When pre-roll completes, content manager becomes active.
 *
 * Without a pre-roll manager:
 *
 * 1. Content AdsManager becomes active directly.
 * 2. If no content manager is available, CONTENT_RESUME_REQUESTED
 *    is emitted synthetically.
 *
 * The player interacts only with this facade and never needs to know
 * which underlying IMA AdsManager is currently active.
 */
export class QPAdsManager {
  private activeAdsManager: google.ima.AdsManager | null = null;

  private activeManagerRole: AdsManagerRole | null = null;

  private nextContentAdsManager: google.ima.AdsManager | null = null;

  private hasPrerollCompleted = false;

  /** Prevents any async operation from activating managers after destroy. */
  private isDestroyed = false;

  /** Prevents duplicate synthetic resume emissions within one session. */
  private syntheticContentResumeEmitted = false;

  private readonly sessionOptions?: QPAdsManagerOptions;
  private readonly logger?: Logger;

  /**
   * Listeners registered by the player.
   * These are automatically rebound whenever the active manager changes.
   */
  private readonly registeredListeners: RegisteredListener[] = [];

  constructor(
    private readonly prerollPrefetchManager?: QPPrefetchManager | null,
    private readonly contentFetcher?: ContentAdsManagerResolver | null,
    options?: QPAdsManagerOptions,
  ) {
    if (!options) {
      return;
    }

    this.sessionOptions = options;
    this.logger = options.logger ?? createLogger(LoggerLevel.OFF);

    for (const { eventType, listener, useCapture = false } of options.eventListeners ?? []) {
      this.addEventListener(eventType, listener, useCapture);
    }

    this.beginAdSession(options);
  }

  // --------------------------------------------------------------------------
  // Public API
  // --------------------------------------------------------------------------

  /**
   * Registers a listener.
   *
   * The listener is stored locally so that it can be rebound automatically
   * when the underlying AdsManager changes.
   */
  public addEventListener(
    eventType: google.ima.AdEvent.Type | google.ima.AdErrorEvent.Type,
    listener: AdEventListener,
    useCapture = false,
  ): void {
    if (this.indexOfRegisteredListener(eventType, listener, useCapture) >= 0) {
      return;
    }

    this.registeredListeners.push({
      eventType,
      listener,
      useCapture,
    });

    this.bindAdsManagerListener(this.activeAdsManager, eventType, listener, useCapture, 'addEventListener');
  }

  /**
   * Removes a previously registered listener.
   */
  public removeEventListener(
    eventType: google.ima.AdEvent.Type | google.ima.AdErrorEvent.Type,
    listener: AdEventListener,
    useCapture = false,
  ): void {
    this.bindAdsManagerListener(this.activeAdsManager, eventType, listener, useCapture, 'removeEventListener');

    const index = this.indexOfRegisteredListener(eventType, listener, useCapture);

    if (index >= 0) {
      this.registeredListeners.splice(index, 1);
    }
  }

  /**
   * Returns the currently active underlying IMA AdsManager.
   */
  public getActiveAdsManager(): google.ima.AdsManager | null {
    return this.activeAdsManager;
  }

  /** Starts the active IMA break after AFC has allowed the opportunity. */
  public start(): void {
    if (this.isDestroyed) {
      return;
    }
    this.activeAdsManager?.start?.();
  }

  /**
   * Destroys the active manager, pending content manager,
   * and clears registered listeners.
   */
  public destroy(): void {
    if (this.isDestroyed) {
      return;
    }

    this.logger?.log('[QPAdsManager] destroy called');

    this.isDestroyed = true;

    this.destroyActiveAdsManager();

    this.destroyNextContentAdsManager();

    this.registeredListeners.length = 0;
  }

  // --------------------------------------------------------------------------
  // Session
  // --------------------------------------------------------------------------

  /**
   * Starts the ad session asynchronously.
   *
   * Constructors cannot be async, so errors are handled here.
   */
  private beginAdSession(options: QPAdsManagerOptions): void {
    void this.startAdSession(options).catch(error => {
      this.logger?.log('[QPAdsManager] Ad session failed', error);
    });
  }

  /**
   * Starts the ad session.
   *
   * IMPORTANT:
   * Pre-roll and content AdsManagers are fetched in parallel.
   */
  private async startAdSession(options: QPAdsManagerOptions): Promise<void> {
    this.logger?.log('[QPAdsManager] Starting ad session');

    /**
     * Start both requests immediately.
     *
     * This avoids:
     *
     * fetch preroll
     *      ↓
     * preroll resolves
     *      ↓
     * fetch content
     *
     * Instead:
     *
     * fetch preroll ──────────┐
     *                         ├── parallel
     * fetch content ──────────┘
     */
    const prerollPromise = this.fetchPrerollAdsManager();
    const contentPromise = this.fetchContentAdsManager();

    const prerollAdsManager = await prerollPromise;

    if (this.isDestroyed) {
      return;
    }

    /**
     * No pre-roll available.
     *
     * Use the content AdsManager directly.
     */
    if (!prerollAdsManager) {
      const contentAdsManager = await contentPromise;

      if (this.isDestroyed) {
        this.destroyAdsManager(contentAdsManager);
        return;
      }

      if (!contentAdsManager) {
        this.logger?.log('[QPAdsManager] No content AdsManager available, resuming content playback');

        this.emitSyntheticContentResume();
        return;
      }

      this.activateAdsManager(contentAdsManager, 'CONTENT', options);

      return;
    }

    /**
     * Pre-roll is available.
     *
     * Activate it immediately.
     */
    this.activateAdsManager(prerollAdsManager, 'PREROLL', options);

    /**
     * Content request is already running.
     *
     * Resolve it in the background.
     */
    void this.resolveContentManager(contentPromise);
  }

  // --------------------------------------------------------------------------
  // Fetching
  // --------------------------------------------------------------------------

  /**
   * Fetches the prefetched pre-roll AdsManager.
   *
   * If this QPAdsManager is destroyed while the request is in flight,
   * the resulting AdsManager is immediately destroyed.
   */
  private async fetchPrerollAdsManager(): Promise<google.ima.AdsManager | null> {
    if (!this.prerollPrefetchManager) {
      return null;
    }

    try {
      const adsManager = await this.prerollPrefetchManager.fetchAdsManager();

      if (this.isDestroyed) {
        this.destroyAdsManager(adsManager);
        return null;
      }

      return adsManager ?? null;
    } catch (error) {
      if (this.contentFetcher) {
        console.warn('[QPAdsManager] Preroll AdsManager failed, falling back to content AdsManager:', error);
      } else {
        console.warn('[QPAdsManager] Preroll AdsManager failed, resuming content playback:', error);

        this.emitSyntheticContentResume();
      }

      this.sessionOptions?.onAdError?.(error);

      return null;
    }
  }

  /**
   * Fetches the content AdsManager.
   *
   * If this QPAdsManager is destroyed while the request is in flight,
   * the resulting AdsManager is immediately destroyed.
   */
  private async fetchContentAdsManager(): Promise<google.ima.AdsManager | null> {
    if (!this.contentFetcher) {
      return null;
    }

    try {
      const adsManager = await this.contentFetcher.fetchAdsManager();

      if (this.isDestroyed) {
        this.destroyAdsManager(adsManager);
        return null;
      }

      return adsManager ?? null;
    } catch (error) {
      this.logger?.log('[QPAdsManager] Content AdsManager fetch failed', error);

      this.sessionOptions?.onAdError?.(error);

      return null;
    }
  }

  /**
   * Resolves the content AdsManager while pre-roll is active.
   *
   * If pre-roll already completed by the time content resolves,
   * the content manager is activated immediately.
   *
   * Otherwise it is stored for the pre-roll completion transition.
   */
  private async resolveContentManager(contentPromise: Promise<google.ima.AdsManager | null>): Promise<void> {
    const contentAdsManager = await contentPromise;

    if (!contentAdsManager) {
      return;
    }

    if (this.isDestroyed) {
      this.destroyAdsManager(contentAdsManager);
      return;
    }

    /**
     * Race protection:
     *
     * Content can finish loading after the pre-roll has already completed.
     */
    if (this.hasPrerollCompleted) {
      this.logger?.log('[QPAdsManager] Content AdsManager loaded after pre-roll completion, activating immediately');

      this.switchToContentAdsManager(contentAdsManager);
      return;
    }

    /**
     * Pre-roll is still playing.
     * Keep content manager ready for the transition.
     */
    this.nextContentAdsManager = contentAdsManager;

    this.logger?.log('[QPAdsManager] Content AdsManager loaded, waiting for pre-roll to finish');
  }

  // --------------------------------------------------------------------------
  // Activation
  // --------------------------------------------------------------------------

  /**
   * Activates an AdsManager and rebinds all player listeners.
   */
  private activateAdsManager(
    adsManager: google.ima.AdsManager,
    role: AdsManagerRole,
    options: QPAdsManagerOptions,
  ): void {
    if (this.isDestroyed) {
      this.destroyAdsManager(adsManager);
      return;
    }

    const { request, imaSDK } = options;

    this.activeAdsManager = adsManager;
    this.activeManagerRole = role;

    this.logger?.log(
      `[QPAdsManager] Activating ${role} AdsManager with ${this.registeredListeners.length} registered listeners`,
    );

    /**
     * Initialize IMA AdsManager.
     */
    adsManager.init?.(request.slotWidth, request.slotHeight, imaSDK?.ViewMode?.NORMAL);

    /**
     * Rebind player listeners.
     */
    this.attachRegisteredListeners(adsManager);

    /**
     * Listen for pre-roll completion.
     */
    if (role === 'PREROLL' && this.resumeEventType) {
      adsManager.addEventListener?.(this.resumeEventType, this.onPrerollCompleted, false);
    }

    if (options.autoPlayAdBreaks !== false) {
      adsManager.start?.();
    }

    /**
     * Notify the caller that this  AdsManager is now active and fully
     * initialised. getCuePoints() is populated at this point, so the
     * caller can safely read and broadcast the cue-point list.
     */
    options.onAdsManagerActivated?.(role);

    /**
     * If a content AdsManager has no pre-roll cue point,
     * content can resume immediately.
     */
    if (role === 'CONTENT' && !this.hasPrerollCuePoint(adsManager, imaSDK)) {
      this.logger?.log('[QPAdsManager] CONTENT manager has no pre-roll, emitting synthetic CONTENT_RESUME_REQUESTED');

      this.emitSyntheticContentResume();
    }
  }

  /**
   * Attaches all registered player listeners to a manager.
   */
  private attachRegisteredListeners(adsManager: google.ima.AdsManager): void {
    for (const { eventType, listener, useCapture } of this.registeredListeners) {
      this.bindAdsManagerListener(adsManager, eventType, listener, useCapture, 'addEventListener');
    }
  }

  /**
   * Removes all registered player listeners from a manager.
   */
  private detachRegisteredListeners(adsManager: google.ima.AdsManager): void {
    for (const { eventType, listener, useCapture } of this.registeredListeners) {
      this.bindAdsManagerListener(adsManager, eventType, listener, useCapture, 'removeEventListener');
    }
  }

  // --------------------------------------------------------------------------
  // Pre-roll completion
  // --------------------------------------------------------------------------

  /**
   * Handles CONTENT_RESUME_REQUESTED from the pre-roll AdsManager.
   *
   * This is treated as the pre-roll → content transition boundary.
   */
  private readonly onPrerollCompleted = (): void => {
    if (this.activeManagerRole !== 'PREROLL' || this.hasPrerollCompleted) {
      return;
    }

    this.hasPrerollCompleted = true;

    const contentAdsManager = this.nextContentAdsManager;

    this.nextContentAdsManager = null;

    if (contentAdsManager) {
      this.logger?.log('[QPAdsManager] Pre-roll completed, switching to loaded content AdsManager');

      this.switchToContentAdsManager(contentAdsManager);

      return;
    }

    /**
     * Content manager is not ready yet.
     *
     * Allow the player to resume content.
     *
     * resolveContentManager() will switch to the content
     * manager when it finishes loading.
     */
    this.logger?.log('[QPAdsManager] Content AdsManager not loaded yet, emitting synthetic CONTENT_RESUME_REQUESTED');

    this.emitSyntheticContentResume();
  };

  // --------------------------------------------------------------------------
  // Manager switching
  // --------------------------------------------------------------------------

  /**
   * Switches from the current manager to the content manager.
   */
  private switchToContentAdsManager(contentAdsManager: google.ima.AdsManager): void {
    if (!this.sessionOptions) {
      this.destroyAdsManager(contentAdsManager);
      return;
    }

    if (this.isDestroyed) {
      this.destroyAdsManager(contentAdsManager);
      return;
    }

    this.logger?.log('[QPAdsManager] Switching active AdsManager to CONTENT');

    this.destroyActiveAdsManager();

    this.activateAdsManager(contentAdsManager, 'CONTENT', this.sessionOptions);
  }

  // --------------------------------------------------------------------------
  // Destroy / cleanup
  // --------------------------------------------------------------------------

  /**
   * Destroys the currently active AdsManager.
   *
   * For PREROLL, recycling must happen before destroy().
   */
  private destroyActiveAdsManager(): void {
    const adsManager = this.activeAdsManager;

    if (!adsManager) {
      return;
    }

    const activeRole = this.activeManagerRole;

    try {
      this.detachRegisteredListeners(adsManager);

      if (activeRole === 'PREROLL' && this.resumeEventType) {
        adsManager.removeEventListener?.(this.resumeEventType, this.onPrerollCompleted, false);
      }
    } catch (error) {
      this.logger?.log('[QPAdsManager] Error removing AdsManager listeners', error);
    }

    /**
     * Important:
     *
     * Recycle pre-roll before calling destroy().
     *
     * QPPrefetchManager may need to remove its own listeners
     * from the AdsManager before it is destroyed.
     */
    if (activeRole === 'PREROLL') {
      this.prerollPrefetchManager?.recyclePrefetchedAdsManager();
    }

    this.destroyAdsManager(adsManager);

    this.activeAdsManager = null;
    this.activeManagerRole = null;
  }

  /**
   * Destroys the pending content manager.
   */
  private destroyNextContentAdsManager(): void {
    if (!this.nextContentAdsManager) {
      return;
    }

    this.destroyAdsManager(this.nextContentAdsManager);

    this.nextContentAdsManager = null;
  }

  /**
   * Safely destroys an AdsManager.
   */
  private destroyAdsManager(adsManager: google.ima.AdsManager | null | undefined): void {
    if (!adsManager) {
      return;
    }

    try {
      adsManager.destroy?.();
    } catch (error) {
      this.logger?.log('[QPAdsManager] Error destroying AdsManager', error);
    }
  }

  // --------------------------------------------------------------------------
  // Helpers
  // --------------------------------------------------------------------------

  /**
   * IMA CONTENT_RESUME_REQUESTED event type.
   */
  private get resumeEventType(): any {
    return this.sessionOptions?.imaSDK?.AdEvent?.Type?.CONTENT_RESUME_REQUESTED;
  }

  /**
   * Finds a registered listener.
   */
  private indexOfRegisteredListener(
    eventType: google.ima.AdEvent.Type | google.ima.AdErrorEvent.Type,
    listener: AdEventListener,
    useCapture: boolean,
  ): number {
    return this.registeredListeners.findIndex(
      item => item.eventType === eventType && item.listener === listener && item.useCapture === useCapture,
    );
  }

  /**
   * Emits CONTENT_RESUME_REQUESTED directly to the player listeners.
   *
   * A copy of the listener list is used so listeners can safely
   * add/remove listeners while the event is being dispatched.
   */
  private emitSyntheticContentResume(): void {
    if (this.syntheticContentResumeEmitted) {
      return;
    }

    const eventType = this.resumeEventType;

    if (!eventType) {
      return;
    }

    this.syntheticContentResumeEmitted = true;

    const syntheticEvent = {
      type: eventType,
    };

    const listeners = this.registeredListeners.filter(item => item.eventType === eventType);

    for (const { listener } of listeners) {
      try {
        listener(syntheticEvent as any);
      } catch (error) {
        this.logger?.log('[QPAdsManager] Error emitting synthetic CONTENT_RESUME_REQUESTED', error);
      }
    }
  }

  private bindAdsManagerListener(
    adsManager: google.ima.AdsManager | null | undefined,
    eventType: google.ima.AdEvent.Type | google.ima.AdErrorEvent.Type,
    listener: AdEventListener,
    useCapture: boolean,
    method: 'addEventListener' | 'removeEventListener',
  ): void {
    adsManager?.[method]?.(eventType as any, listener as any, useCapture);
  }

  /**
   * Checks whether the AdsManager contains a pre-roll cue point.
   */
  private hasPrerollCuePoint(adsManager: google.ima.AdsManager, imaSDK: typeof google.ima): boolean {
    try {
      const cuePoints = adsManager?.getCuePoints?.();

      return Array.isArray(cuePoints) && cuePoints.includes(imaSDK?.AdCuePoints?.PREROLL ?? 0);
    } catch {
      return false;
    }
  }
}
